import { applyDiminishingToScore, getDiminishingPenalty } from '@/data/survivorSynergyOverrides'
import { areSurvivorGameplayEquivalents, getSurvivorGameplayPerkId } from '@/data/survivorPerkEquivalents'
import {
  functionalRedundancyGroups,
  functionalRedundancyPolicy,
  getSurvivorCentralProfile,
  getSurvivorCentralProfileAudit,
  getSurvivorCentralWantWeight,
  getSurvivorPerkRoleIds,
  getSurvivorRoleRecipe,
  getSurvivorRoleRecipeMatches,
  getSurvivorRoleRecipeMatch
} from '@/data/survivorPerkRoles'
import {
  getSurvivorInteraction,
  getSurvivorInteractionWeight,
  isSurvivorBestBuildPairBlocked
} from '@/data/survivorPerkInteractions'

const SLOT_COUNT = 4
const CHAOS_ZERO_PRIMARY_CENTRAL_LINK = 35
const CHAOS_ZERO_UNDER_SPECIFIED_PENALTY = 140
const CHAOS_ZERO_LEGACY_CAP = 18
const CHAOS_ZERO_SHARED_TAG_CAP = 8
const CHAOS_ZERO_GENERIC_GLUE_TAGS = ['aura', 'teamplay', 'item', 'chase']
const CHAOS_ZERO_COMBO_POOL_LIMIT = 30
const CHAOS_ZERO_VARIANT_SCORE_BAND = 35
const CHAOS_ZERO_RECIPE_POOL_PER_SLOT = 6
const MIN_INFLUENCE_COEFFICIENT = 0.01
const MAX_INFLUENCE_COEFFICIENT = 1
const INFLUENCE_ANCHORS = [
  {
    chaos: 0,
    satellite1: 0.2,
    satellite2: 0.05
  },
  {
    chaos: 25,
    satellite1: 0.5,
    satellite2: 0.25
  },
  {
    chaos: 100,
    satellite1: 1,
    satellite2: 1
  }
]

function normalizeConfig (config) {
  const normalizedConfig = Object.assign({
    lockedPerks: [],
    blacklist: [],
    ownedOnly: false,
    ownedPerks: [],
    activeGameplayFilters: [],
    allowedTags: null,
    preservedSlots: [],
    rerollPerkId: null,
    chaos: 25,
    chaosZeroComboRank: 0
  }, config)
  normalizedConfig.chaos = clampChaos(normalizedConfig.chaos)
  return normalizedConfig
}

function createErrorResult (errors) {
  return {
    status: 'invalid-config',
    errors: errors,
    slots: [],
    influenceChain: []
  }
}

function interpolate (start, end, progress) {
  return start + (end - start) * progress
}

function clampInfluenceCoefficient (coefficient) {
  return Number(Math.max(MIN_INFLUENCE_COEFFICIENT, Math.min(MAX_INFLUENCE_COEFFICIENT, coefficient)).toFixed(4))
}

export function getInfluenceCoefficients (chaos) {
  const normalizedChaos = clampChaos(chaos)
  const upperAnchorIndex = normalizedChaos <= INFLUENCE_ANCHORS[1].chaos ? 1 : 2
  const lowerAnchor = INFLUENCE_ANCHORS[upperAnchorIndex - 1]
  const upperAnchor = INFLUENCE_ANCHORS[upperAnchorIndex]
  const progress = (normalizedChaos - lowerAnchor.chaos) / (upperAnchor.chaos - lowerAnchor.chaos)

  return {
    central: 1,
    satellite1: clampInfluenceCoefficient(interpolate(lowerAnchor.satellite1, upperAnchor.satellite1, progress)),
    satellite2: clampInfluenceCoefficient(interpolate(lowerAnchor.satellite2, upperAnchor.satellite2, progress))
  }
}

function getInfluenceCoefficient (index, influenceCoefficients) {
  if (index === 0) return influenceCoefficients.central
  if (index === 1) return influenceCoefficients.satellite1
  return influenceCoefficients.satellite2
}

function buildPerkLookup (perks) {
  return perks.reduce((lookup, perk) => {
    lookup[perk.id] = perk
    return lookup
  }, {})
}

function validateConfig (perksById, config) {
  const errors = []
  const lockedIds = new Set()
  const lockedSlots = new Set()
  const blacklist = new Set(config.blacklist)
  const ownedPerks = new Set(config.ownedPerks)

  if (config.lockedPerks.length > SLOT_COUNT) {
    errors.push({ code: 'TOO_MANY_LOCKS' })
  }

  config.lockedPerks.forEach(lock => {
    if (!Number.isInteger(lock.slot) || lock.slot < 1 || lock.slot > SLOT_COUNT) {
      errors.push({ code: 'INVALID_LOCKED_SLOT', slot: lock.slot })
    }
    if (lockedSlots.has(lock.slot)) {
      errors.push({ code: 'DUPLICATE_LOCKED_SLOT', slot: lock.slot })
    }
    if (!perksById[lock.perkId]) {
      errors.push({ code: 'UNKNOWN_LOCKED_PERK', perkId: lock.perkId })
    }
    if (lockedIds.has(lock.perkId)) {
      errors.push({ code: 'DUPLICATE_LOCKED_PERK', perkId: lock.perkId })
    }
    if (blacklist.has(lock.perkId)) {
      errors.push({ code: 'LOCKED_PERK_BLACKLISTED', perkId: lock.perkId })
    }
    if (config.ownedOnly && !ownedPerks.has(lock.perkId)) {
      errors.push({ code: 'LOCKED_PERK_NOT_OWNED', perkId: lock.perkId })
    }
    lockedSlots.add(lock.slot)
    lockedIds.add(lock.perkId)
  })

  return errors
}

function resolveRelation (firstPerk, secondPerk, relationName) {
  const firstRelations = firstPerk[relationName] || {}
  const secondRelations = secondPerk[relationName] || {}
  const firstGameplayId = getSurvivorGameplayPerkId(firstPerk.id)
  const secondGameplayId = getSurvivorGameplayPerkId(secondPerk.id)
  return firstRelations[secondPerk.id] || firstRelations[secondGameplayId] || secondRelations[firstPerk.id] || secondRelations[firstGameplayId]
}

function hasHardAntiSynergy (candidate, selectedPerks) {
  return selectedPerks.some(selectedPerk => {
    const mechanicRule = getSurvivorInteraction(candidate, selectedPerk)
    if (mechanicRule && mechanicRule.kind === 'anti_synergy' && mechanicRule.severity === 'hard') return true
    const relation = resolveRelation(candidate, selectedPerk, 'antiSynergies')
    return relation && relation.severity === 'hard'
  })
}

function isAllowedCandidate (candidate, context) {
  if (context.excludedIds.has(candidate.id)) return false
  if (context.blacklist.has(candidate.id)) return false
  if (context.ownedOnly && !context.ownedPerks.has(candidate.id)) return false
  if (hasHardAntiSynergy(candidate, context.selectedPerks)) return false
  if (context.allowedTags && candidate.tags.length > 0 && !candidate.tags.some(tag => context.allowedTags.includes(tag))) return false
  return true
}

function getCandidatePool (perks, context) {
  return perks.filter(perk => isAllowedCandidate(perk, context))
}

function pickUniformly (candidates, random) {
  if (candidates.length === 0) return null
  return candidates[Math.floor(random() * candidates.length)]
}

function getSharedTagScore (firstPerk, secondPerk) {
  const sharedTags = firstPerk.tags.filter(tag => secondPerk.tags.indexOf(tag) !== -1)
  return Math.min(sharedTags.length * 8, 20)
}

function getCompatibilityDetails (influentialPerk, candidate) {
  const mechanicWeight = getSurvivorInteractionWeight(influentialPerk, candidate)
  if (mechanicWeight !== null) {
    return {
      compatibility: mechanicWeight,
      source: 'mechanic-contract'
    }
  }
  const relation = resolveRelation(influentialPerk, candidate, 'synergyWeights')
  if (typeof relation === 'number') {
    return {
      compatibility: relation,
      source: 'synergyWeights'
    }
  }
  if (relation && typeof relation.weight === 'number') {
    return {
      compatibility: relation.weight,
      source: 'synergyWeights'
    }
  }
  return {
    compatibility: getSharedTagScore(influentialPerk, candidate),
    source: 'shared-tags-fallback'
  }
}

function getScoringCompatibilityDetails (influence, candidate, config, centralInfluence) {
  if (config.chaos === 0 && centralInfluence && influence.perkId === centralInfluence.perkId) {
    const centralLink = getCentralLink(influence.perk, candidate, config)
    return {
      compatibility: centralLink.centralDirectWeight,
      source: centralLink.contributionType
    }
  }
  return getCompatibilityDetails(influence.perk, candidate)
}

function normalizeContributionType (source) {
  return source === 'shared-tags-fallback' ? 'sharedTagsFallback' : source
}

function getDirectionalSynergyWeight (firstPerk, secondPerk) {
  const mechanicWeight = getSurvivorInteractionWeight(firstPerk, secondPerk)
  if (mechanicWeight !== null) return mechanicWeight
  const relations = firstPerk.synergyWeights || {}
  const relation = relations[secondPerk.id] || relations[getSurvivorGameplayPerkId(secondPerk.id)]
  if (typeof relation === 'number') return relation
  if (relation && typeof relation.weight === 'number') return relation.weight
  return 0
}

function getTagDiagnostics (candidate, allowedTags) {
  if (!allowedTags) {
    return {
      filtered: false,
      allowed: candidate.tags.slice(),
      excluded: []
    }
  }
  return {
    filtered: true,
    allowed: candidate.tags.filter(tag => allowedTags.indexOf(tag) !== -1),
    excluded: candidate.tags.filter(tag => allowedTags.indexOf(tag) === -1)
  }
}

function getBaseScore (candidate, config) {
  let score = 10 + candidate.soloQueueScore * 0.2
  const activeFilters = config.activeGameplayFilters

  activeFilters.forEach(filter => {
    if (candidate.tags.indexOf(filter) !== -1) score += 24
  })

  if (candidate.constraints.requiresTeamCoordination) score -= 10
  if (candidate.constraints.requiresItem && activeFilters.indexOf('item') === -1) score -= 7
  if (candidate.constraints.requiresHeal && activeFilters.indexOf('support') === -1) score -= 3
  if (activeFilters.indexOf('meme') === -1) {
    score -= candidate.memeLevel * ((100 - config.chaos) / 100) * 0.08
  }

  return score
}

function isGenericGlueCandidate (candidate) {
  return candidate.tags.length > 0 && candidate.tags.every(tag => CHAOS_ZERO_GENERIC_GLUE_TAGS.indexOf(tag) !== -1)
}

function getChaosZeroLegacyLink (centralPerk, candidate) {
  const centralProfile = getSurvivorCentralProfile(centralPerk.id)
  const allowLegacyFallback = Boolean(centralProfile && centralProfile.allowLegacyFallback)
  const compatibility = getDirectionalSynergyWeight(centralPerk, candidate)
  if (!compatibility) {
    return {
      contributionType: 'sharedTagsFallback',
      weight: Math.min(allowLegacyFallback ? 16 : CHAOS_ZERO_SHARED_TAG_CAP, getSharedTagScore(centralPerk, candidate))
    }
  }
  const centralRoles = getSurvivorPerkRoleIds(centralPerk.id)
  const candidateRoles = getSurvivorPerkRoleIds(candidate.id)
  const sharesRole = candidateRoles.some(role => centralRoles.indexOf(role) !== -1)
  const identityScale = sharesRole ? 0.35 : 0.25
  const glueScale = isGenericGlueCandidate(candidate) ? 0.65 : 1
  return {
    contributionType: 'synergyWeights',
    weight: Math.min(allowLegacyFallback ? 34 : CHAOS_ZERO_LEGACY_CAP, compatibility * identityScale * glueScale)
  }
}

function getCentralLink (centralPerk, candidate, config) {
  const centralWantWeight = getSurvivorCentralWantWeight(centralPerk.id, candidate.id)
  const roleRecipeMatch = getSurvivorRoleRecipeMatch(centralPerk.id, candidate.id)
  const legacyLink = getChaosZeroLegacyLink(centralPerk, candidate)
  const centralDirect = centralWantWeight !== null
    ? {
      contributionType: 'centralWants',
      weight: centralWantWeight
    }
    : roleRecipeMatch
      ? {
        contributionType: roleRecipeMatch.generated ? 'fallbackRoleRecipe' : 'roleRecipe',
        weight: roleRecipeMatch.weight
      }
      : legacyLink
  return {
    perk: candidate,
    centralDirectWeight: centralDirect.weight,
    centralDirectSource: centralDirect.contributionType,
    contributionType: centralDirect.contributionType,
    roleRecipeRole: centralDirect.contributionType === 'roleRecipe' || centralDirect.contributionType === 'fallbackRoleRecipe' ? roleRecipeMatch.candidateRole : null,
    roleRecipeCentralRole: centralDirect.contributionType === 'roleRecipe' || centralDirect.contributionType === 'fallbackRoleRecipe' ? roleRecipeMatch.centralRole : null,
    roleRecipeSlot: centralDirect.contributionType === 'roleRecipe' || centralDirect.contributionType === 'fallbackRoleRecipe' ? roleRecipeMatch.slotName : null,
    baseScore: getBaseScore(candidate, config)
  }
}

function compareCentralLinks (first, second) {
  const directDifference = second.centralDirectWeight - first.centralDirectWeight
  if (directDifference !== 0) return directDifference
  const baseDifference = second.baseScore - first.baseScore
  if (baseDifference !== 0) return baseDifference
  return first.perk.id.localeCompare(second.perk.id)
}

function getPairwiseRaw (satellites) {
  let total = 0
  for (let firstIndex = 0; firstIndex < satellites.length; firstIndex++) {
    for (let secondIndex = firstIndex + 1; secondIndex < satellites.length; secondIndex++) {
      const firstPerk = satellites[firstIndex].perk
      const secondPerk = satellites[secondIndex].perk
      total += (getDirectionalSynergyWeight(firstPerk, secondPerk) + getDirectionalSynergyWeight(secondPerk, firstPerk)) / 2
    }
  }
  return total
}

function getThemeCohesionBonus (centralPerk, satellites) {
  const perks = [centralPerk].concat(satellites.map(satellite => satellite.perk))
  let matchingPairs = 0
  for (let firstIndex = 0; firstIndex < perks.length; firstIndex++) {
    for (let secondIndex = firstIndex + 1; secondIndex < perks.length; secondIndex++) {
      if (getSharedTagScore(perks[firstIndex], perks[secondIndex]) > 0) matchingPairs++
    }
  }
  return Math.min(matchingPairs * 3, 15)
}

function getLowCentralPenalty (satellites) {
  return satellites.reduce((total, satellite) => {
    if (satellite.centralDirectWeight < 25) return total + 35
    if (satellite.centralDirectWeight < CHAOS_ZERO_PRIMARY_CENTRAL_LINK) return total + 20
    return total
  }, 0)
}

function getGlobalDiminishingPenalty (centralPerk, satellites) {
  const orderedSatellites = satellites.slice().sort(compareCentralLinks)
  const currentBuildIds = [centralPerk.id]
  return orderedSatellites.reduce((total, satellite) => {
    const diminishing = getDiminishingPenalty(satellite.perk.id, currentBuildIds)
    currentBuildIds.push(satellite.perk.id)
    return total + satellite.centralDirectWeight * (1 - diminishing.multiplier)
  }, 0)
}

function getFunctionalRedundancyPenalty (centralPerk, satellites) {
  const perkIds = [centralPerk.id].concat(satellites.map(satellite => satellite.perk.id))
  return Object.keys(functionalRedundancyGroups).reduce((total, group) => {
    const memberCount = perkIds.filter(perkId => functionalRedundancyGroups[group].indexOf(perkId) !== -1).length
    const policy = functionalRedundancyPolicy[group]
    return total + Math.max(0, memberCount - policy.freeCount) * policy.penaltyPerExtra
  }, 0)
}

function getCandidateFunctionalRedundancyPenalty (candidateId, currentBuildIds) {
  return Object.keys(functionalRedundancyGroups).reduce((total, group) => {
    const groupMembers = functionalRedundancyGroups[group]
    if (groupMembers.indexOf(candidateId) === -1) return total

    const policy = functionalRedundancyPolicy[group]
    const currentCount = currentBuildIds.filter(perkId => groupMembers.indexOf(perkId) !== -1).length
    const currentOverage = Math.max(0, currentCount - policy.freeCount)
    const nextOverage = Math.max(0, currentCount + 1 - policy.freeCount)
    return total + Math.max(0, nextOverage - currentOverage) * policy.penaltyPerExtra
  }, 0)
}

function getDriftPenalty (centralPerk, satellites) {
  const centralRoles = getSurvivorPerkRoleIds(centralPerk.id)
  return satellites.reduce((total, satellite) => {
    if (satellite.contributionType === 'centralWants' || satellite.contributionType === 'roleRecipe') return total
    const satelliteRoles = getSurvivorPerkRoleIds(satellite.perk.id)
    const sharesRole = satelliteRoles.some(role => centralRoles.indexOf(role) !== -1)
    return total + (sharesRole ? 0 : 12)
  }, 0)
}

function isStrongCentralLink (satellite) {
  return satellite.contributionType === 'centralWants' || satellite.contributionType === 'roleRecipe'
}

function getUnderSpecifiedPenalty (satellites) {
  const requiredStrongLinks = Math.min(2, satellites.length)
  const strongLinkCount = satellites.filter(isStrongCentralLink).length
  return {
    strongLinkCount: strongLinkCount,
    requiredStrongLinks: requiredStrongLinks,
    penalty: Math.max(0, requiredStrongLinks - strongLinkCount) * CHAOS_ZERO_UNDER_SPECIFIED_PENALTY
  }
}

function getForbiddenRolePenalty (centralPerk, satellites) {
  const centralProfile = getSurvivorCentralProfile(centralPerk.id)
  if (!centralProfile || !centralProfile.forbiddenRoles.length) return 0
  return satellites.reduce((total, satellite) => {
    const roles = getSurvivorPerkRoleIds(satellite.perk.id)
    return total + (roles.some(role => centralProfile.forbiddenRoles.indexOf(role) !== -1) ? 35 : 0)
  }, 0)
}

function getCentralProfileQaFlags (centralProfileAudit) {
  const flags = []
  if (!centralProfileAudit.explicitProfile) flags.push('DEFAULT_CENTRAL_PROFILE')
  if (!centralProfileAudit.explicitCentralWants) flags.push('NO_EXPLICIT_CENTRAL_WANTS')
  if (!centralProfileAudit.hasRecipe) flags.push('NO_ROLE_RECIPE')
  if (!centralProfileAudit.forbiddenRoles.length) flags.push('NO_FORBIDDEN_ROLES')
  return flags
}

function compareRoleSlotCoverage (first, second) {
  if (!first) return second
  if (second.coveredSlots.length !== first.coveredSlots.length) {
    return second.coveredSlots.length > first.coveredSlots.length ? second : first
  }
  if (second.totalWeight !== first.totalWeight) return second.totalWeight > first.totalWeight ? second : first
  return first
}

function getRoleSlotCoverage (centralPerk, satellites) {
  const recipe = getSurvivorRoleRecipe(centralPerk.id)
  if (!recipe) {
    return {
      coveredSlots: [],
      missingSlots: [],
      assignments: {},
      bonus: 0,
      penalty: 0
    }
  }

  function assignSlots (satelliteIndex, usedSlots, assignments, totalWeight) {
    if (satelliteIndex >= satellites.length) {
      return {
        coveredSlots: Array.from(usedSlots),
        assignments: assignments,
        totalWeight: totalWeight
      }
    }

    const satellite = satellites[satelliteIndex]
    const matches = getSurvivorRoleRecipeMatches(centralPerk.id, satellite.perk.id)
      .filter(match => !usedSlots.has(match.slotName))
    let best = assignSlots(satelliteIndex + 1, usedSlots, Object.assign({}, assignments), totalWeight)
    matches.forEach(match => {
      const nextUsedSlots = new Set(usedSlots)
      nextUsedSlots.add(match.slotName)
      const nextAssignments = Object.assign({}, assignments)
      nextAssignments[satellite.perk.id] = match
      best = compareRoleSlotCoverage(best, assignSlots(satelliteIndex + 1, nextUsedSlots, nextAssignments, totalWeight + match.weight))
    })
    return best
  }

  const coverage = assignSlots(0, new Set(), {}, 0)
  const missingSlots = recipe.slotNeeds.filter(slotNeed => coverage.coveredSlots.indexOf(slotNeed.name) === -1).map(slotNeed => slotNeed.name)
  return Object.assign({}, coverage, {
    missingSlots: missingSlots,
    bonus: coverage.coveredSlots.length * 20,
    penalty: missingSlots.length * 40
  })
}

function getChaosZeroFitLabel (satellite, underSpecified) {
  if (underSpecified.penalty > 0 && !isStrongCentralLink(satellite)) return 'Under-Specified'
  if (satellite.contributionType === 'centralWants') return 'Core Fit'
  if (satellite.contributionType === 'roleRecipe') return 'Recipe Fit'
  if (satellite.contributionType === 'fallbackRoleRecipe') return 'Fallback Recipe'
  if (satellite.contributionType === 'synergyWeights') return 'Weak Fit'
  return 'Fallback'
}

function scoreGlobalCombo (centralPerk, satellites, qaFlags) {
  const centralDirectTotal = satellites.reduce((total, satellite) => total + satellite.centralDirectWeight, 0)
  const pairwiseRaw = getPairwiseRaw(satellites)
  const pairwiseBonus = Math.min(pairwiseRaw * 0.15, centralDirectTotal * 0.20)
  const themeCohesionBonus = getThemeCohesionBonus(centralPerk, satellites)
  const diminishingPenaltyTotal = getGlobalDiminishingPenalty(centralPerk, satellites)
  const functionalRedundancyPenalty = getFunctionalRedundancyPenalty(centralPerk, satellites)
  const driftPenalty = getDriftPenalty(centralPerk, satellites)
  const lowCentralPenalty = getLowCentralPenalty(satellites)
  const underSpecified = getUnderSpecifiedPenalty(satellites)
  const forbiddenRolePenalty = getForbiddenRolePenalty(centralPerk, satellites)
  const roleSlotCoverage = getRoleSlotCoverage(centralPerk, satellites)
  const comboQaFlags = qaFlags.slice()
  if (underSpecified.penalty > 0) comboQaFlags.push('UNDER_SPECIFIED_CENTRAL')
  return {
    satellites: satellites,
    globalComboScore: centralDirectTotal + pairwiseBonus + themeCohesionBonus + roleSlotCoverage.bonus - diminishingPenaltyTotal - functionalRedundancyPenalty - driftPenalty - lowCentralPenalty - underSpecified.penalty - forbiddenRolePenalty - roleSlotCoverage.penalty,
    centralDirectTotal: centralDirectTotal,
    weakestCentralDirectWeight: satellites.length ? Math.min(...satellites.map(satellite => satellite.centralDirectWeight)) : 0,
    pairwiseBonus: pairwiseBonus,
    themeCohesionBonus: themeCohesionBonus,
    diminishingPenaltyTotal: diminishingPenaltyTotal,
    functionalRedundancyPenalty: functionalRedundancyPenalty,
    driftPenalty: driftPenalty,
    lowCentralPenalty: lowCentralPenalty,
    underSpecifiedPenalty: underSpecified.penalty,
    strongLinkCount: underSpecified.strongLinkCount,
    requiredStrongLinks: underSpecified.requiredStrongLinks,
    forbiddenRolePenalty: forbiddenRolePenalty,
    roleSlotCoverage: roleSlotCoverage,
    qaFlags: comboQaFlags
  }
}

function canJoinChaosZeroCombo (candidate, centralPerk, forcedSatellites, selectedSatellites) {
  const selectedPerks = [centralPerk]
    .concat(forcedSatellites.map(satellite => satellite.perk))
    .concat(selectedSatellites.map(satellite => satellite.perk))
  if (selectedPerks.some(perk => areSurvivorGameplayEquivalents(perk.id, candidate.id))) return false
  if (selectedPerks.some(perk => isSurvivorBestBuildPairBlocked(perk.id, candidate.id))) return false
  return !hasHardAntiSynergy(candidate, selectedPerks)
}

function globalComboStrength (entry) {
  return entry.combo.satellites.reduce((sum, satellite) => sum + (satellite.perk.strengthScore || satellite.perk.soloQueueScore || 50), 0)
}

function compareGlobalComboEntries (first, second) {
  if (second.combo.globalComboScore !== first.combo.globalComboScore) return second.combo.globalComboScore - first.combo.globalComboScore
  if (second.combo.weakestCentralDirectWeight !== first.combo.weakestCentralDirectWeight) return second.combo.weakestCentralDirectWeight - first.combo.weakestCentralDirectWeight
  const strengthDifference = globalComboStrength(second) - globalComboStrength(first)
  return strengthDifference || first.order - second.order
}

function generatedComboDistance (first, second) {
  const firstIds = new Set(first.generated.map(satellite => satellite.perk.id))
  const overlap = second.generated.filter(satellite => firstIds.has(satellite.perk.id)).length
  return Math.max(first.generated.length, second.generated.length) - overlap
}

function selectDiverseGlobalCombos (rankedCombos, limit) {
  if (!rankedCombos.length) return []
  const selected = [rankedCombos[0]]
  const bestScore = rankedCombos[0].combo.globalComboScore
  const qualityBand = rankedCombos.filter(entry => entry.combo.globalComboScore >= bestScore - CHAOS_ZERO_VARIANT_SCORE_BAND)
  const closestStrongAlternative = qualityBand.slice(1).find(entry => generatedComboDistance(selected[0], entry) >= 1)
  if (closestStrongAlternative && selected.length < limit) selected.push(closestStrongAlternative)
  const diverseAlternative = qualityBand.slice(1).find(entry => {
    if (selected.indexOf(entry) !== -1) return false
    return generatedComboDistance(selected[0], entry) >= Math.min(2, selected[0].generated.length) &&
      selected.slice(1).every(chosen => generatedComboDistance(chosen, entry) >= 1)
  })
  if (diverseAlternative && selected.length < limit) selected.push(diverseAlternative)
  qualityBand.forEach(entry => {
    if (selected.length >= limit || selected.indexOf(entry) !== -1) return
    selected.push(entry)
  })
  rankedCombos.forEach(entry => {
    if (selected.length >= limit || selected.indexOf(entry) !== -1) return
    selected.push(entry)
  })
  return selected
}

function createChaosZeroPlan (perks, config, candidateContext, slots, locksBySlot, preservedBySlot, influenceChain, startSlotIndex, pool) {
  const centralPerk = influenceChain[0].perk
  const centralProfileAudit = getSurvivorCentralProfileAudit(centralPerk.id)
  const generatedSlotIndexes = []
  const forcedSatellites = influenceChain.slice(1).map(influence => getCentralLink(centralPerk, influence.perk, config))
  const forcedIds = new Set(forcedSatellites.map(satellite => satellite.perk.id))
  const qaFlags = getCentralProfileQaFlags(centralProfileAudit)

  for (let slotIndex = startSlotIndex; slotIndex < SLOT_COUNT; slotIndex++) {
    const lock = locksBySlot[slotIndex]
    const preserved = preservedBySlot[slotIndex]
    if (lock && lock.influenceScoring && !forcedIds.has(lock.perkId)) {
      const lockedPerk = candidateContext.selectedPerks.find(perk => perk.id === lock.perkId)
      if (!lockedPerk) continue
      const satellite = getCentralLink(centralPerk, lockedPerk, config)
      forcedSatellites.push(satellite)
      forcedIds.add(lock.perkId)
      if (satellite.centralDirectWeight < CHAOS_ZERO_PRIMARY_CENTRAL_LINK) qaFlags.push('LOCK_LOW_CENTRAL_LINK')
    } else if (preserved && preserved.influential && !forcedIds.has(preserved.perkId)) {
      const preservedPerk = perks.find(perk => perk.id === preserved.perkId)
      if (!preservedPerk) continue
      const satellite = getCentralLink(centralPerk, preservedPerk, config)
      forcedSatellites.push(satellite)
      forcedIds.add(preserved.perkId)
    } else if (!lock && !preserved) {
      generatedSlotIndexes.push(slotIndex)
    }
  }

  const allCandidateLinks = pool
    .filter(perk => !forcedIds.has(perk.id))
    .filter(perk => canJoinChaosZeroCombo(perk, centralPerk, forcedSatellites, []))
    .map(perk => getCentralLink(centralPerk, perk, config))
    .sort(compareCentralLinks)
  const candidateLinksById = new Map()
  allCandidateLinks.slice(0, CHAOS_ZERO_COMBO_POOL_LIMIT).forEach(link => candidateLinksById.set(link.perk.id, link))
  const roleRecipe = getSurvivorRoleRecipe(centralPerk.id)
  if (roleRecipe) {
    roleRecipe.slotNeeds.forEach(slotNeed => {
      allCandidateLinks
        .filter(link => getSurvivorRoleRecipeMatches(centralPerk.id, link.perk.id).some(match => match.slotName === slotNeed.name))
        .slice(0, CHAOS_ZERO_RECIPE_POOL_PER_SLOT)
        .forEach(link => candidateLinksById.set(link.perk.id, link))
    })
  }
  const candidateLinks = Array.from(candidateLinksById.values())

  const rankedCombos = []
  let comboCandidateCount = 0

  function visitCombinations (startIndex, chosen) {
    if (chosen.length === generatedSlotIndexes.length) {
      const combo = scoreGlobalCombo(centralPerk, forcedSatellites.concat(chosen), qaFlags)
      comboCandidateCount++
      rankedCombos.push({
        combo,
        generated: chosen.slice(),
        order: comboCandidateCount
      })
      return
    }

    const remaining = generatedSlotIndexes.length - chosen.length
    for (let candidateIndex = startIndex; candidateIndex <= candidateLinks.length - remaining; candidateIndex++) {
      const candidate = candidateLinks[candidateIndex]
      if (!canJoinChaosZeroCombo(candidate.perk, centralPerk, forcedSatellites, chosen)) continue
      visitCombinations(candidateIndex + 1, chosen.concat(candidate))
    }
  }

  visitCombinations(0, [])
  rankedCombos.sort(compareGlobalComboEntries)
  const diverseCombos = selectDiverseGlobalCombos(rankedCombos, 3)
  if (!diverseCombos.length) return null
  const requestedComboRank = Math.max(0, Math.floor(Number(config.chaosZeroComboRank) || 0))
  const selectedEntry = diverseCombos[requestedComboRank % diverseCombos.length]
  const selectedCombo = selectedEntry.combo
  const selectedGenerated = selectedEntry.generated
  const selectedRawRank = rankedCombos.indexOf(selectedEntry) + 1

  if (selectedCombo.satellites.some(satellite => satellite.centralDirectWeight < CHAOS_ZERO_PRIMARY_CENTRAL_LINK)) {
    selectedCombo.qaFlags.push('LOW_CENTRAL_LINK')
  }
  if (selectedCombo.underSpecifiedPenalty > 0) selectedCombo.qaFlags.push('UNDER_SPECIFIED_CENTRAL')

  selectedCombo.satellites.forEach(satellite => {
    const roleSlotAssignment = selectedCombo.roleSlotCoverage.assignments[satellite.perk.id]
    satellite.qaLabel = getChaosZeroFitLabel(satellite, { penalty: selectedCombo.underSpecifiedPenalty })
    satellite.roleSlotName = roleSlotAssignment ? roleSlotAssignment.slotName : null
    satellite.roleSlotRole = roleSlotAssignment ? roleSlotAssignment.candidateRole : null
  })

  const generatedBySlot = {}
  selectedGenerated.slice().sort(compareCentralLinks).forEach((satellite, index) => {
    generatedBySlot[generatedSlotIndexes[index]] = satellite
  })

  const centralDirectShares = selectedCombo.satellites.reduce((shares, satellite) => {
    shares[satellite.perk.id] = selectedCombo.centralDirectTotal > 0 ? satellite.centralDirectWeight / selectedCombo.centralDirectTotal : 0
    return shares
  }, {})
  const hasPartialPlan = selectedCombo.lowCentralPenalty > 0 || selectedCombo.roleSlotCoverage.missingSlots.length > 0

  return {
    generatedBySlot,
    debug: Object.assign({}, selectedCombo, {
      selectionMode: 'chaos0-global-combo',
      comboCandidateCount,
      selectedComboRank: selectedRawRank,
      selectedVariantRank: diverseCombos.indexOf(selectedEntry) + 1,
      diverseComboCount: diverseCombos.length,
      comboQualityLabel: selectedCombo.underSpecifiedPenalty > 0 ? 'Under-Specified' : hasPartialPlan ? 'Partial' : 'Specified',
      qaFlags: Array.from(new Set(selectedCombo.qaFlags)),
      centralProfileAudit,
      centralDirectShares,
      satellites: selectedCombo.satellites.map(satellite => ({
        perkId: satellite.perk.id,
        centralDirectWeight: satellite.centralDirectWeight,
        centralDirectShare: centralDirectShares[satellite.perk.id],
        contributionType: satellite.contributionType,
        roleRecipeRole: satellite.roleRecipeRole,
        roleRecipeCentralRole: satellite.roleRecipeCentralRole,
        roleSlotName: satellite.roleSlotName,
        roleSlotRole: satellite.roleSlotRole,
        qaLabel: satellite.qaLabel
      }))
    })
  }
}
function scoreCandidate (candidate, influenceChain, config) {
  const baseScore = getBaseScore(candidate, config)
  let score = baseScore
  const contributions = []
  const centralInfluence = influenceChain[0]

  influenceChain.forEach(influence => {
    const compatibilityDetails = getScoringCompatibilityDetails(influence, candidate, config, centralInfluence)
    const compatibility = compatibilityDetails.compatibility
    const contribution = compatibility * influence.coefficient
    score += contribution
    if (compatibility > 0) {
      contributions.push({
        perkId: influence.perk.id,
        coefficient: influence.coefficient,
        compatibility: compatibility,
        contribution: contribution,
        source: compatibilityDetails.source,
        contributionType: normalizeContributionType(compatibilityDetails.source)
      })
    }
  })

  const currentBuildIds = influenceChain.map(influence => influence.perkId)
  const functionalRedundancyPenalty = getCandidateFunctionalRedundancyPenalty(candidate.id, currentBuildIds)
  score = Math.max(1, score - functionalRedundancyPenalty)
  const scoreBeforeDiminishing = score
  const diminishing = getDiminishingPenalty(candidate.id, currentBuildIds)
  const scoreAfterDiminishing = Math.max(1, scoreBeforeDiminishing * diminishing.multiplier)
  score = applyDiminishingToScore(scoreBeforeDiminishing, candidate.id, currentBuildIds)

  return {
    perk: candidate,
    score: score,
    scoring: {
      selectionType: 'weighted-satellite',
      baseScore: baseScore,
      scoreBeforeDiminishing: scoreBeforeDiminishing,
      finalScore: score,
      functionalRedundancyPenalty: functionalRedundancyPenalty,
      contributions: contributions,
      diminishing: Object.assign({}, diminishing, {
        penalty: Math.max(0, scoreBeforeDiminishing - scoreAfterDiminishing),
        roundingDelta: score - scoreAfterDiminishing
      }),
      tags: getTagDiagnostics(candidate, config.allowedTags)
    }
  }
}

function clampChaos (chaos) {
  return Math.max(0, Math.min(100, Number(chaos) || 0))
}

function getDirectCentralContribution (candidate, centralPerkId) {
  const contribution = candidate.scoring.contributions.find(contribution => contribution.perkId === centralPerkId)
  return contribution ? contribution.contribution : 0
}

function getCumulativeSynergyContribution (candidate) {
  return candidate.scoring.contributions.reduce((total, contribution) => total + contribution.contribution, 0)
}

function compareRankedCandidates (first, second, centralPerkId) {
  const scoreDifference = second.score - first.score
  if (scoreDifference !== 0) return scoreDifference

  const centralContributionDifference = getDirectCentralContribution(second, centralPerkId) - getDirectCentralContribution(first, centralPerkId)
  if (centralContributionDifference !== 0) return centralContributionDifference

  const cumulativeSynergyDifference = getCumulativeSynergyContribution(second) - getCumulativeSynergyContribution(first)
  if (cumulativeSynergyDifference !== 0) return cumulativeSynergyDifference

  return first.perk.id.localeCompare(second.perk.id)
}

function rankCandidates (scoredCandidates, centralPerkId) {
  return scoredCandidates.slice().sort((first, second) => compareRankedCandidates(first, second, centralPerkId))
}

function pickWeightedSatellite (scoredCandidates, chaos, random, centralPerkId) {
  if (scoredCandidates.length === 0) return null

  const normalizedChaos = clampChaos(chaos)
  if (normalizedChaos === 0) return addCandidateMetrics(rankCandidates(scoredCandidates, centralPerkId)[0], scoredCandidates, centralPerkId)

  const bestScore = Math.max(...scoredCandidates.map(candidate => candidate.score))
  const allowedGap = 14 + normalizedChaos * 1.1
  const eligible = scoredCandidates.filter(candidate => candidate.score >= bestScore - allowedGap)
  const floorScore = bestScore - allowedGap
  const exponent = 3 - normalizedChaos * 0.0225
  const weights = eligible.map(candidate => Math.pow(Math.max(candidate.score - floorScore, 1), exponent))
  const weightTotal = weights.reduce((total, weight) => total + weight, 0)
  let position = random() * weightTotal

  for (let i = 0; i < eligible.length; i++) {
    position -= weights[i]
    if (position <= 0) return addCandidateMetrics(eligible[i], scoredCandidates, centralPerkId)
  }

  return addCandidateMetrics(eligible[eligible.length - 1], scoredCandidates, centralPerkId)
}

function addCandidateMetrics (selected, scoredCandidates, centralPerkId) {
  const rankedCandidates = rankCandidates(scoredCandidates, centralPerkId)
  const candidateRank = rankedCandidates.findIndex(candidate => candidate.perk.id === selected.perk.id) + 1
  const candidateCount = rankedCandidates.length
  selected.scoring.qa = {
    candidateRank: candidateRank,
    candidateCount: candidateCount,
    candidatePercentile: candidateCount <= 1 ? 100 : Number((((candidateCount - candidateRank) / (candidateCount - 1)) * 100).toFixed(2)),
    topCandidateRaw: rankedCandidates[0].score,
    selectedRaw: selected.score,
    topCandidates: rankedCandidates.slice(0, 5).map(candidate => ({
      perkId: candidate.perk.id,
      raw: candidate.score
    }))
  }
  return selected
}

function createSlot (slotIndex, perk, source, coefficient, scoring) {
  return {
    slot: slotIndex + 1,
    perk: perk,
    perkId: perk.id,
    source: source,
    influential: coefficient !== null,
    influenceCoefficient: coefficient,
    scoring: scoring || null
  }
}

function addInfluence (influenceChain, perk, source, influenceCoefficients) {
  const influence = {
    perk: perk,
    perkId: perk.id,
    coefficient: getInfluenceCoefficient(influenceChain.length, influenceCoefficients),
    source: source
  }
  influenceChain.push(influence)
  return influence
}

export function evaluateSurvivorChaosZeroCombo (perks, centralPerkId, satellitePerkIds) {
  const perksById = buildPerkLookup(perks)
  const centralPerk = perksById[centralPerkId]
  const satelliteIds = satellitePerkIds || []
  if (!centralPerk || satelliteIds.length !== SLOT_COUNT - 1) return null
  if (new Set([centralPerkId].concat(satelliteIds)).size !== SLOT_COUNT) return null

  const satellites = satelliteIds.map(perkId => perksById[perkId])
  if (satellites.some(perk => !perk)) return null

  const completeBuild = [centralPerk].concat(satellites)
  for (let firstIndex = 0; firstIndex < completeBuild.length; firstIndex++) {
    for (let secondIndex = firstIndex + 1; secondIndex < completeBuild.length; secondIndex++) {
      const firstPerk = completeBuild[firstIndex]
      const secondPerk = completeBuild[secondIndex]
      if (areSurvivorGameplayEquivalents(firstPerk.id, secondPerk.id)) return null
      if (isSurvivorBestBuildPairBlocked(firstPerk.id, secondPerk.id)) return null
      if (hasHardAntiSynergy(secondPerk, [firstPerk])) return null
    }
  }

  const config = normalizeConfig({ chaos: 0 })
  const centralProfileAudit = getSurvivorCentralProfileAudit(centralPerk.id)
  const links = satellites.map(perk => getCentralLink(centralPerk, perk, config))
  return scoreGlobalCombo(
    centralPerk,
    links,
    getCentralProfileQaFlags(centralProfileAudit)
  )
}

export default function generateSurvivorBuild (perks, buildConfig, random) {
  const config = normalizeConfig(buildConfig)
  const influenceCoefficients = getInfluenceCoefficients(config.chaos)
  const randomValue = random || Math.random
  const perksById = buildPerkLookup(perks)
  const errors = validateConfig(perksById, config)

  if (errors.length > 0) return createErrorResult(errors)

  const slots = new Array(SLOT_COUNT).fill(null)
  const influenceChain = []
  const lockedIds = new Set(config.lockedPerks.map(lock => lock.perkId))
  const excludedIds = new Set(lockedIds)
  const selectedPerks = config.lockedPerks.map(lock => perksById[lock.perkId])
  const locksBySlot = config.lockedPerks.reduce((locks, lock) => {
    locks[lock.slot - 1] = lock
    return locks
  }, {})
  const preservedBySlot = config.preservedSlots.reduce((preservedSlots, slot) => {
    preservedSlots[slot.slot - 1] = slot
    return preservedSlots
  }, {})
  const candidateContext = {
    blacklist: new Set(config.blacklist),
    ownedOnly: config.ownedOnly,
    ownedPerks: new Set(config.ownedPerks),
    allowedTags: config.allowedTags,
    excludedIds: excludedIds,
    selectedPerks: selectedPerks
  }
  let chaosZeroPlan = null

  let replacementGenerated = false
  for (let slotIndex = 0; slotIndex < SLOT_COUNT; slotIndex++) {
    const lock = locksBySlot[slotIndex]
    if (lock) {
      const perk = perksById[lock.perkId]
      const contextualCandidate = lock.influenceScoring && influenceChain.length > 0
        ? scoreCandidate(perk, influenceChain, config)
        : null
      const contextualScoring = contextualCandidate
        ? addCandidateMetrics(contextualCandidate, getCandidatePool(perks, candidateContext)
          .map(candidate => scoreCandidate(candidate, influenceChain, config))
          .concat(contextualCandidate), influenceChain[0].perkId).scoring
        : null
      const influence = lock.influenceScoring ? addInfluence(influenceChain, perk, 'locked-influential', influenceCoefficients) : null
      slots[slotIndex] = createSlot(slotIndex, perk, lock.influenceScoring ? 'locked-influential' : 'locked-passive', influence ? influence.coefficient : null, Object.assign({}, contextualScoring || {}, {
        selectionType: 'locked',
        influenceScoring: Boolean(lock.influenceScoring),
        contextualScore: Boolean(contextualScoring),
        tags: getTagDiagnostics(perk, config.allowedTags)
      }))
      continue
    }

    const preservedSlot = preservedBySlot[slotIndex]
    if (preservedSlot) {
      const perk = perksById[preservedSlot.perkId]
      if (!perk || !isAllowedCandidate(perk, candidateContext)) {
        return createErrorResult([{ code: 'INVALID_PRESERVED_PERK', perkId: preservedSlot.perkId, slot: preservedSlot.slot }])
      }
      const influence = preservedSlot.influential ? addInfluence(influenceChain, perk, 'preserved', influenceCoefficients) : null
      slots[slotIndex] = createSlot(slotIndex, perk, 'preserved', influence ? influence.coefficient : null, {
        selectionType: 'preserved',
        influenceScoring: Boolean(preservedSlot.influential),
        tags: getTagDiagnostics(perk, config.allowedTags)
      })
      selectedPerks.push(perk)
      excludedIds.add(perk.id)
      continue
    }

    let pool = getCandidatePool(perks, candidateContext)
    let selected
    let source

    if (!replacementGenerated && config.rerollPerkId) {
      const replacementPool = pool.filter(perk => perk.id !== config.rerollPerkId)
      if (replacementPool.length > 0) pool = replacementPool
    }

    if (influenceChain.length === 0) {
      const centralPool = pool.filter(perk => perk.centralEligible)
      selected = pickUniformly(centralPool, randomValue)
      source = 'generated-central'
    } else if (config.chaos === 0) {
      if (!chaosZeroPlan) {
        chaosZeroPlan = createChaosZeroPlan(perks, config, candidateContext, slots, locksBySlot, preservedBySlot, influenceChain, slotIndex, pool)
      }
      const plannedSatellite = chaosZeroPlan && chaosZeroPlan.generatedBySlot[slotIndex]
      if (plannedSatellite) {
        selected = addCandidateMetrics(
          scoreCandidate(plannedSatellite.perk, influenceChain, config),
          pool.map(perk => scoreCandidate(perk, influenceChain, config)),
          influenceChain[0].perkId
        )
        selected.scoring.selectionMode = chaosZeroPlan.debug.selectionMode
        selected.scoring.globalComboScore = chaosZeroPlan.debug.globalComboScore
        selected.scoring.centralDirectWeight = plannedSatellite.centralDirectWeight
        selected.scoring.centralDirectShare = chaosZeroPlan.debug.centralDirectShares[plannedSatellite.perk.id]
        if (plannedSatellite.plannedQa) {
          selected.scoring.qa = Object.assign({}, selected.scoring.qa, plannedSatellite.plannedQa)
        }
        selected.scoring.chaosZeroCentralLink = {
          contributionType: plannedSatellite.contributionType,
          centralDirectWeight: plannedSatellite.centralDirectWeight,
          roleRecipeRole: plannedSatellite.roleRecipeRole,
          roleRecipeCentralRole: plannedSatellite.roleRecipeCentralRole,
          roleSlotName: plannedSatellite.roleSlotName,
          roleSlotRole: plannedSatellite.roleSlotRole,
          qaLabel: plannedSatellite.qaLabel
        }
        selected.scoring.qa.priorityLabel = plannedSatellite.qaLabel
        selected.scoring.qa.comboQualityLabel = chaosZeroPlan.debug.comboQualityLabel
        const centralContribution = selected.scoring.contributions.find(contribution => contribution.perkId === influenceChain[0].perkId)
        if (centralContribution) {
          centralContribution.rawContributionSource = centralContribution.source
          centralContribution.source = plannedSatellite.contributionType
          centralContribution.contributionType = plannedSatellite.contributionType
        }
        selected.scoring.selectedComboRank = chaosZeroPlan.debug.selectedComboRank
      }
      source = 'generated-satellite'
    } else {
      selected = pickWeightedSatellite(pool.map(perk => scoreCandidate(perk, influenceChain, config)), config.chaos, randomValue, influenceChain[0].perkId)
      source = 'generated-satellite'
    }

    if (!selected) {
      return createErrorResult([{ code: 'NO_VALID_CANDIDATE', slot: slotIndex + 1 }])
    }

    const selectedPerk = selected.perk || selected
    const influence = addInfluence(influenceChain, selectedPerk, source, influenceCoefficients)
    const scoring = selected.scoring || {
      selectionType: 'uniform-central',
      finalScore: null,
      tags: getTagDiagnostics(selectedPerk, config.allowedTags)
    }
    slots[slotIndex] = createSlot(slotIndex, selectedPerk, source, influence.coefficient, scoring)
    selectedPerks.push(selectedPerk)
    excludedIds.add(selectedPerk.id)
    replacementGenerated = true
  }

  return {
    status: 'success',
    errors: [],
    slots: slots,
    influenceChain: influenceChain,
    scoring: {
      chaos: config.chaos,
      influenceCoefficients: influenceCoefficients,
      allowedTags: config.allowedTags ? config.allowedTags.slice() : null,
      comboDebug: chaosZeroPlan ? chaosZeroPlan.debug : null
    }
  }
}
